import type { ButtonProps } from '@mui/material/Button';
import { buttonClasses } from '@mui/material/Button';
import type { Components, ComponentsVariants, CSSObject, Theme } from '@mui/material/styles';
import { varAlpha } from 'minimal-shared/utils';

// ----------------------------------------------------------------------

/**
 * TypeScript (type definition and extension)
 * @to {@link file://./../../extend-theme-types.d.ts}
 */

export type ButtonExtendVariant = {
  soft: true;
};

// ----------------------------------------------------------------------

const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

type PaletteColor = (typeof COLORS)[number];

// Hover steps either side of `main` that the generated palette ramp does not provide.
const INK_HOVER = '#333535';
const ACCENT_HOVER_DARK = '#6373FF';
const DANGER_HOVER_DARK = '#F0605A';

// ----------------------------------------------------------------------

function styleColors(ownerState: ButtonProps, styles: (val: PaletteColor) => CSSObject) {
  const outputStyle = COLORS.reduce((acc, color) => {
    if (!ownerState.disabled && ownerState.color === color) {
      acc = styles(color);
    }
    return acc;
  }, {});

  return outputStyle;
}

// ----------------------------------------------------------------------

const MuiButtonBase: Components<Theme>['MuiButtonBase'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: { root: ({ theme }) => ({ fontFamily: theme.typography.fontFamily }) },
};

// ----------------------------------------------------------------------

const softVariant: Record<string, NonNullable<ComponentsVariants<Theme>['MuiButton']>> = {
  base: [
    {
      props: ({ ownerState }) => ownerState.variant === 'soft',
      style: ({ theme }) => ({
        [`&.${buttonClasses.disabled}`]: {
          backgroundColor: theme.vars.palette.action.disabledBackground,
        },
        [`& .${buttonClasses.loadingPositionStart}`]: { left: 14 },
        [`& .${buttonClasses.loadingPositionEnd}`]: { right: 14 },
        [`&.${buttonClasses.sizeSmall}`]: {
          [`& .${buttonClasses.loadingPositionStart}`]: { left: 10 },
          [`& .${buttonClasses.loadingPositionEnd}`]: { right: 10 },
        },
      }),
    },
  ],
  colors: COLORS.map((color) => ({
    props: ({ ownerState }) => !ownerState.disabled && ownerState.variant === 'soft' && ownerState.color === color,
    style: ({ theme }) => ({
      color: theme.vars.palette[color].dark,
      backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.16),
      '&:hover': { backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.32) },
      ...theme.applyStyles('dark', {
        color: theme.vars.palette[color].light,
      }),
    }),
  })),
  inheritColor: [
    {
      props: ({ ownerState }) =>
        !ownerState.disabled && ownerState.variant === 'soft' && ownerState.color === 'inherit',
      style: ({ theme }) => ({
        color: theme.vars.palette.text.primary,
        backgroundColor: '#F0EFEC',
        '&:hover': { backgroundColor: '#E7E6E2' },
        ...theme.applyStyles('dark', {
          backgroundColor: '#1C1C1C',
          '&:hover': { backgroundColor: '#242424' },
        }),
      }),
    },
  ],
  primaryColor: [
    {
      props: ({ ownerState }) =>
        !ownerState.disabled && ownerState.variant === 'soft' && ownerState.color === 'primary',
      style: ({ theme }) => ({
        color: theme.vars.palette.primary.main,
        backgroundColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.1),
        border: `1px solid ${varAlpha(theme.vars.palette.primary.mainChannel, 0.35)}`,
        '&:hover': {
          backgroundColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.16),
          borderColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.55),
        },
        ...theme.applyStyles('dark', {
          color: theme.vars.palette.primary.light,
          backgroundColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.18),
          borderColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.45),
          '&:hover': {
            backgroundColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.26),
            borderColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.6),
          },
        }),
      }),
    },
  ],
  errorColor: [
    {
      props: ({ ownerState }) => !ownerState.disabled && ownerState.variant === 'soft' && ownerState.color === 'error',
      style: ({ theme }) => ({
        color: '#D8433C',
        backgroundColor: theme.vars.palette.error.lighter,
        '&:hover': { backgroundColor: '#FBE0E0' },
        ...theme.applyStyles('dark', {
          color: theme.vars.palette.error.light,
          backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.16),
          '&:hover': { backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.24) },
        }),
      }),
    },
  ],
};

const MuiButton: Components<Theme>['MuiButton'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { color: 'inherit', disableElevation: true },

  // v9 moved variant arrays off of `styleOverrides.root` and onto a
  // sibling `variants` key on the component config.
  variants: [
    softVariant.base,
    softVariant.colors,
    softVariant.inheritColor,
    softVariant.primaryColor,
    softVariant.errorColor,
  ].flat(),
  styleOverrides: {
    root: {
      fontSize: '13px',
      fontWeight: 500,
      borderRadius: '999px',
    },
    startIcon: { marginRight: '6px', marginLeft: 0 },
    endIcon: { marginLeft: '6px', marginRight: 0 },
    /**
     * @variant contained
     */
    contained: ({ theme, ownerState }) => {
      const isInherit = ownerState.color === 'inherit' && !ownerState.disabled;
      const isPrimary = ownerState.color === 'primary' && !ownerState.disabled;
      const isError = ownerState.color === 'error' && !ownerState.disabled;
      const styled = {
        // Hover is a flat highlight, not a coloured glow: darken the fill one step and keep the
        // shadow off. The previous `customShadows[color]` drop shadow read as a blue halo.
        colors: styleColors(ownerState, (color) => ({
          '&:hover': {
            backgroundColor: theme.vars.palette[color].dark,
            boxShadow: 'none',
          },
        })),
        // The accent fill is reserved for booking calls to action, so a filled button with no
        // explicit colour is ink on the page rather than brand blue.
        inheritColor: {
          ...(isInherit && {
            color: theme.vars.palette.background.default,
            backgroundColor: theme.vars.palette.text.primary,
            '&:hover': { boxShadow: 'none', backgroundColor: INK_HOVER },
            ...theme.applyStyles('dark', {
              '&:hover': { boxShadow: 'none', backgroundColor: theme.vars.palette.common.white },
            }),
          }),
        },
        primaryColor: {
          ...(isPrimary && {
            color: theme.vars.palette.common.white,
            backgroundColor: theme.vars.palette.primary.main,
            '&:hover': { boxShadow: 'none', backgroundColor: theme.vars.palette.primary.dark },
            ...theme.applyStyles('dark', {
              '&:hover': { boxShadow: 'none', backgroundColor: ACCENT_HOVER_DARK },
            }),
          }),
        },
        errorColor: {
          ...(isError && {
            color: theme.vars.palette.common.white,
            backgroundColor: theme.vars.palette.error.main,
            '&:hover': { boxShadow: 'none', backgroundColor: theme.vars.palette.error.dark },
            ...theme.applyStyles('dark', {
              '&:hover': { boxShadow: 'none', backgroundColor: DANGER_HOVER_DARK },
            }),
          }),
        },
      };
      return {
        ...styled.colors,
        ...styled.primaryColor,
        ...styled.errorColor,
        ...styled.inheritColor,
      };
    },
    /**
     * @variant outlined
     */
    outlined: ({ theme, ownerState }) => {
      const isInherit = ownerState.color === 'inherit' && !ownerState.disabled;
      const isPrimary = ownerState.color === 'primary' && !ownerState.disabled;
      const isError = ownerState.color === 'error' && !ownerState.disabled;
      const styled = {
        colors: styleColors(ownerState, (color) => ({
          borderColor: varAlpha(theme.vars.palette[color].mainChannel, 0.48),
        })),
        inheritColor: {
          ...(isInherit && {
            color: theme.vars.palette.text.primary,
            borderColor: '#E2E2E2',
            '&:hover': { backgroundColor: 'transparent', borderColor: '#BFBFBF' },
            ...theme.applyStyles('dark', {
              borderColor: '#2B2B2B',
              '&:hover': { backgroundColor: 'transparent', borderColor: '#3E3E3E' },
            }),
          }),
        },
        primaryColor: {
          ...(isPrimary && {
            color: theme.vars.palette.primary.main,
            backgroundColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.1),
            borderColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.35),
            '&:hover': {
              backgroundColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.16),
              borderColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.55),
            },
            ...theme.applyStyles('dark', {
              color: theme.vars.palette.primary.light,
              backgroundColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.18),
              borderColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.45),
              '&:hover': {
                backgroundColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.26),
                borderColor: varAlpha(theme.vars.palette.primary.mainChannel, 0.6),
              },
            }),
          }),
        },
        errorColor: {
          ...(isError && {
            color: theme.vars.palette.error.main,
            borderColor: '#F3CFCD',
            '&:hover': { backgroundColor: '#FEF7F7', borderColor: '#E5A9A6' },
            ...theme.applyStyles('dark', {
              color: theme.vars.palette.error.light,
              borderColor: varAlpha(theme.vars.palette.error.mainChannel, 0.4),
              '&:hover': {
                backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.1),
                borderColor: varAlpha(theme.vars.palette.error.mainChannel, 0.4),
              },
            }),
          }),
        },
        // Hover fills with a neutral tint and leaves the border as-is, rather than drawing a
        // `currentColor` ring — a primary/blue outline no longer lights up blue on hover.
        base: {
          '&:hover': { backgroundColor: theme.vars.palette.action.hover, boxShadow: 'none' },
        },
      };
      return {
        ...styled.base,
        ...styled.colors,
        ...styled.primaryColor,
        ...styled.errorColor,
        ...styled.inheritColor,
      };
    },
    /**
     * @variant text
     */
    text: ({ ownerState, theme }) => {
      const styled = {
        // Every text button hovers to the same neutral tint (MUI's default would tint colored text
        // buttons with their own colour, e.g. blue for primary — kept neutral to match the ticket).
        colors: styleColors(ownerState, (color) => ({
          color: theme.vars.palette[color].main,
          '&:hover': { backgroundColor: theme.vars.palette.action.hover },
        })),
        inheritColor: {
          ...(ownerState.color === 'inherit' &&
            !ownerState.disabled && {
              color: theme.vars.palette.text.primary,
              '&:hover': { backgroundColor: theme.vars.palette.action.hover },
            }),
        },
      };
      return { ...styled.colors, ...styled.inheritColor };
    },
    /**
     * @sizes
     */
    sizeSmall: ({ ownerState }) => ({
      height: 28,
      ...(ownerState.variant === 'text'
        ? { paddingLeft: '8px', paddingRight: '8px' }
        : { paddingLeft: '12px', paddingRight: '12px' }),
    }),
    sizeMedium: ({ ownerState }) => ({
      height: 32,
      ...(ownerState.variant === 'text'
        ? { paddingLeft: '10px', paddingRight: '10px' }
        : { paddingLeft: '14px', paddingRight: '14px' }),
    }),
    sizeLarge: ({ ownerState }) => ({
      height: 40,
      fontSize: '14px',
      ...(ownerState.variant === 'text'
        ? { paddingLeft: '12px', paddingRight: '12px' }
        : { paddingLeft: '18px', paddingRight: '18px' }),
    }),
  },
};

// ----------------------------------------------------------------------

export const button = { MuiButtonBase, MuiButton };
